/**
 * AI Image Processing Service
 * Extracts data from invoice images using OpenAI Vision API
 */

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

/**
 * Check if AI processing is available
 */
export const isAIAvailable = () => {
  return !!OPENAI_API_KEY;
};

/**
 * Get AI usage instructions
 */
export const getAIUsageInstructions = () => {
  return {
    available: isAIAvailable(),
    instructions: isAIAvailable() 
      ? "🤖 AI-powered image extraction enabled"
      : "⚠️ AI extraction not available - add REACT_APP_OPENAI_API_KEY to .env"
  };
};

/**
 * Convert image file to base64
 */
const convertImageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Process image with AI to extract invoice data
 */
export const processImageWithAI = async (file) => {
  if (!isAIAvailable()) {
    throw new Error("AI processing not available. Please add REACT_APP_OPENAI_API_KEY to your .env file.");
  }

  try {
    // Convert image to base64
    const base64Image = await convertImageToBase64(file);
    
    // Prepare the prompt for invoice data extraction
    const prompt = `
    Analyze this invoice image and extract the following information in JSON format:
    
    {
      "invoiceNumber": "Invoice number or reference",
      "date": "Invoice date in DD/MM/YYYY format",
      "supplier": "Supplier/company name",
      "supplierAddress": "Supplier address",
      "supplierEmail": "Supplier email",
      "supplierPhone": "Supplier phone number",
      "totalHT": "Total amount excluding tax (number)",
      "tva": "Tax amount (number)",
      "totalTTC": "Total amount including tax (number)",
      "products": [
        {
          "name": "Product name",
          "description": "Product description",
          "quantity": "Quantity (number)",
          "priceHT": "Unit price excluding tax (number)",
          "tva": "Tax rate percentage (number)",
          "remise": "Discount percentage (number, default 0)"
        }
      ]
    }
    
    Important:
    - Extract dates in DD/MM/YYYY format
    - Convert all monetary amounts to numbers (remove currency symbols)
    - Extract all products from the invoice
    - If information is not found, use null or empty string
    - Ensure all numbers are properly formatted
    - Extract supplier information from the company issuing the invoice
    `;

    // Call OpenAI Vision API
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: prompt
              },
              {
                type: 'image_url',
                image_url: {
                  url: `data:image/jpeg;base64,${base64Image}`,
                  detail: 'high'
                }
              }
            ]
          }
        ],
        max_tokens: 2000,
        temperature: 0.1
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`OpenAI API error: ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error('No content received from AI');
    }

    // Parse the JSON response
    let extractedData;
    try {
      // Extract JSON from the response (in case there's additional text)
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        extractedData = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('No JSON found in response');
      }
    } catch (parseError) {
      console.error('Error parsing AI response:', parseError);
      console.error('AI Response:', content);
      throw new Error('Failed to parse AI response as JSON');
    }

    // Validate and clean the extracted data
    const cleanedData = {
      invoiceNumber: extractedData.invoiceNumber || '',
      date: extractedData.date || '',
      supplier: extractedData.supplier || '',
      supplierAddress: extractedData.supplierAddress || '',
      supplierEmail: extractedData.supplierEmail || '',
      supplierPhone: extractedData.supplierPhone || '',
      totalHT: parseFloat(extractedData.totalHT) || 0,
      tva: parseFloat(extractedData.tva) || 0,
      totalTTC: parseFloat(extractedData.totalTTC) || 0,
      products: (extractedData.products || []).map(product => ({
        name: product.name || '',
        description: product.description || '',
        quantity: parseFloat(product.quantity) || 1,
        priceHT: parseFloat(product.priceHT) || 0,
        tva: parseFloat(product.tva) || 20,
        remise: parseFloat(product.remise) || 0
      }))
    };

    console.log('AI Image Processing Result:', cleanedData);
    return cleanedData;

  } catch (error) {
    console.error('Error processing image with AI:', error);
    throw error;
  }
};

/**
 * Fallback regex-based image processing (if AI is not available)
 */
export const processImageWithRegex = async (file) => {
  // This is a placeholder for regex-based image processing
  // In a real implementation, you would use OCR libraries like Tesseract.js
  // For now, return empty data
  console.warn('Regex-based image processing not implemented. Please use AI processing.');
  
  return {
    invoiceNumber: '',
    date: '',
    supplier: '',
    supplierAddress: '',
    supplierEmail: '',
    supplierPhone: '',
    totalHT: 0,
    tva: 0,
    totalTTC: 0,
    products: []
  };
};

/**
 * Main image processing function with AI and fallback
 */
export const processImage = async (file) => {
  if (isAIAvailable()) {
    return await processImageWithAI(file);
  } else {
    return await processImageWithRegex(file);
  }
};
